import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect, useContext } from 'react';
import logo from '../images/logo.png';
import { formatTrainerId, formatName } from '../utils/dataTransformers';
import {
  FaChalkboardTeacher,
  FaClipboardList,
  FaMoneyBillWave,
  FaFileInvoiceDollar,
  FaUserCircle,
  FaSignOutAlt,
  FaChevronDown,
  FaCog,
  FaIdBadge,
  FaUsers,
  FaToggleOn,
  FaToggleOff,
  FaCalendarAlt,
  FaCrown,
  FaStar,
  FaStoreAlt,
  FaBoxOpen,
  FaChartLine,
  FaDollarSign,
  FaStore
} from 'react-icons/fa';
import Avatar from './Avatar';
import UserContext from './UserContext';
import Logout from './Logout';
import HttpInterceptor from '../lib/HttpInterceptor';
import NotificationCenter from './NotificationCenter';

function TrainerHeader() {
  const navigate = useNavigate()
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);
  const profileRef = useRef(null);
  const serviceDropdownRef = useRef(null);
  const profileTimeoutRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { session: trainer, setSession } = useContext(UserContext);
  const [isReseller, setIsReseller] = useState(false);
  const [resellerDropdownOpen, setResellerDropdownOpen] = useState(false);

  // Check if user is a reseller
  useEffect(() => {
    const checkResellerStatus = async () => {
      // Use trainer ID or check if trainer object exists
      if (!trainer) return;
      try {
        const response = await HttpInterceptor.get('/api/resellers/profile');
        if (response.data?.success && response.data?.data) {
          setIsReseller(response.data.data.status === 'approved');
        }
      } catch (error) {
        // User is not a reseller or not approved
        setIsReseller(false);
      }
    };
    checkResellerStatus();
  }, [trainer]);

  // Get services and servicePartnerTypes from trainer session
  const trainerServices = (trainer?.services && trainer.services.length > 0) ? trainer.services : (trainer?.service ? [trainer.service] : []);
  const servicePartnerTypes = trainer?.servicePartnerTypes || {};

  // Get active service from localStorage or default to first service
  const [activeService, setActiveService] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('activeService');
      if (saved && trainerServices.includes(saved)) {
        return saved;
      }
    }
    return trainerServices[0] || null;
  });

  // Update activeService when trainer services change
  useEffect(() => {
    if (trainerServices.length > 0 && (!activeService || !trainerServices.includes(activeService))) {
      const saved = localStorage.getItem('activeService');
      if (saved && trainerServices.includes(saved)) {
        setActiveService(saved);
      } else {
        setActiveService(trainerServices[0]);
        localStorage.setItem('activeService', trainerServices[0]);
      }
    }
  }, [trainerServices, activeService]);

  // Get partner type for active service
  const activePartnerType = activeService ? (servicePartnerTypes[activeService] || 'instructor') : 'instructor';
  const isConsultant = activePartnerType === 'consultant';

  // Check if user has both instructor and consultant services
  const hasInstructorServices = trainerServices.some(service =>
    (servicePartnerTypes[service] || 'instructor') === 'instructor'
  );
  const hasConsultantServices = trainerServices.some(service =>
    (servicePartnerTypes[service] || 'instructor') === 'consultant'
  );

  // Check if trainer has both types (show toggle)
  const hasBothTypes = hasInstructorServices && hasConsultantServices;

  // Get current dashboard preference - sync with current route
  const location = useLocation();
  const [dashboardPreference, setDashboardPreference] = useState(() => {
    // If on consultant dashboard, preference should be consultant
    if (location.pathname.includes('/trainer/consultant-dashboard')) {
      return 'consultant';
    }
    return localStorage.getItem('dashboardPreference') || 'instructor';
  });

  // Update preference when route changes
  useEffect(() => {
    if (location.pathname.includes('/trainer/consultant-dashboard')) {
      setDashboardPreference('consultant');
      localStorage.setItem('dashboardPreference', 'consultant');
    } else if (location.pathname.includes('/trainer/home')) {
      setDashboardPreference('instructor');
      localStorage.setItem('dashboardPreference', 'instructor');
    }
  }, [location.pathname]);

  // Check if trainer is on advance plan
  const isAdvancePlan = trainer?.subscriptionPlan === 'advance';

  // Handle dashboard toggle
  const handleDashboardToggle = () => {
    const newPreference = dashboardPreference === 'instructor' ? 'consultant' : 'instructor';
    setDashboardPreference(newPreference);
    localStorage.setItem('dashboardPreference', newPreference);

    // Navigate to the appropriate dashboard
    if (newPreference === 'consultant') {
      navigate('/trainer/consultant-dashboard', { replace: true });
    } else {
      navigate('/trainer/home', { replace: true });
    }
  };

  // Handle service change
  const handleServiceChange = (service) => {
    setActiveService(service);
    localStorage.setItem('activeService', service);
    setIsServiceDropdownOpen(false);
    // Optionally redirect to home when service changes
    if (window.location.pathname.includes('/trainer/classes') && servicePartnerTypes[service] === 'consultant') {
      navigate('/trainer/appointments');
    } else if (window.location.pathname.includes('/trainer/appointments') && servicePartnerTypes[service] === 'instructor') {
      navigate('/trainer/classes');
    }
  };
  // Check screen size and set mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdowns when clicking outside or when cursor leaves
  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
      if (serviceDropdownRef.current && !serviceDropdownRef.current.contains(event.target)) {
        setIsServiceDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      clearTimeout(profileTimeoutRef.current);
    };
  }, []);

  const handleProfileInteraction = () => {
    clearTimeout(profileTimeoutRef.current);
    setIsProfileOpen(true);
  };

  const handleProfileLeave = () => {
    profileTimeoutRef.current = setTimeout(() => {
      setIsProfileOpen(false);
    }, 200);
  };

  const handleDropdownEnter = () => {
    clearTimeout(profileTimeoutRef.current);
  };

  const handleDropdownLeave = () => {
    setIsProfileOpen(false);
  };


  return (
    <header className="sticky top-0 z-50 bg-gradient-to-r from-slate-800 via-blue-900 to-slate-900 text-white shadow-2xl animate-fade-in border-b-2 border-cyan-400/40">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-2">
        {/* Logo and Brand */}
        <div className="flex items-center">
          <Link to="/trainer/home" className="relative group/logo">
            <div className="absolute -inset-1 bg-gradient-to-br from-cyan-300/40 via-teal-300/30 to-transparent blur-[12px] rounded-full opacity-70 group-hover/logo:opacity-100 transition-opacity duration-300"></div>
            <div className="relative transform group-hover/logo:scale-[1.02] transition-transform duration-300">
              <img
                src={logo}
                alt="Yogvital Logo"
                className="h-15 w-auto drop-shadow-[0_2px_4px_rgba(255,255,255,0.3)]"
              />
            </div>
          </Link>

          <Link to="/trainer/home" className="ml-3 group/text flex items-center gap-2">
            <h1 className="text-3xl font-bold mt-1 font-display group-hover/text:text-cyan-300 transition-colors duration-300 drop-shadow-lg">
              Yogvital
            </h1>
            {isAdvancePlan && (
              <span className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-semibold rounded-full shadow-lg animate-pulse">
                <FaCrown className="text-xs" />
                <span className="hidden sm:inline">Advance Plan</span>
              </span>
            )}
          </Link>
        </div>

        {/* Desktop Menu - now shows at 769px and above */}
        <div className={`${isMobile ? 'hidden' : 'flex'} items-center space-x-2`}>
          {/* Dashboard Toggle - Only show if trainer has both instructor and consultant services */}
          {hasBothTypes && (
            <div className="relative mr-2">
              <button
                onClick={handleDashboardToggle}
                className="flex items-center px-3 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 border border-cyan-300/50 bg-cyan-900/30"
                title={`Switch to ${dashboardPreference === 'instructor' ? 'Consultant' : 'Instructor'} Dashboard`}
              >
                {dashboardPreference === 'instructor' ? (
                  <>
                    <FaChalkboardTeacher className="mr-2 text-white" />
                    <span className="font-medium text-sm mr-2">Instructor</span>
                    <FaToggleOff className="text-white/80" />
                  </>
                ) : (
                  <>
                    <FaCalendarAlt className="mr-2 text-white" />
                    <span className="font-medium text-sm mr-2">Consultant</span>
                    <FaToggleOn className="text-white/80" />
                  </>
                )}
              </button>
            </div>
          )}

          {/* Reseller Menu Dropdown */}
          {isReseller ? (
            <div
              className="relative mr-2"
              onMouseEnter={() => !isMobile && setResellerDropdownOpen(true)}
              onMouseLeave={() => !isMobile && setResellerDropdownOpen(false)}
            >
              <button
                onClick={() => isMobile && setResellerDropdownOpen(!resellerDropdownOpen)}
                className="flex items-center px-3 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 border border-cyan-300/50 bg-cyan-900/30"
              >
                <FaStoreAlt className="mr-2 text-white" />
                <span className="font-medium text-sm mr-2">Reseller</span>
                <FaChevronDown className={`text-white/80 text-xs transition-transform ${resellerDropdownOpen ? 'transform rotate-180' : ''}`} />
              </button>
              {resellerDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-md shadow-lg py-1 z-50">
                  <Link
                    to="/reseller/dashboard"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setResellerDropdownOpen(false)}
                  >
                    <FaChartLine className="mr-3 text-gray-500" />
                    <span>Dashboard</span>
                  </Link>
                  <Link
                    to="/reseller/products"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setResellerDropdownOpen(false)}
                  >
                    <FaStore className="mr-3 text-gray-500" />
                    <span>My Products</span>
                  </Link>
                  <Link
                    to="/reseller/orders"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setResellerDropdownOpen(false)}
                  >
                    <FaBoxOpen className="mr-3 text-gray-500" />
                    <span>My Orders</span>
                  </Link>
                  <Link
                    to="/reseller/earnings"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setResellerDropdownOpen(false)}
                  >
                    <FaDollarSign className="mr-3 text-gray-500" />
                    <span>Earnings</span>
                  </Link>
                </div>
              )}
            </div>
          ) : null}

          {/* Service Switcher - Only show if multiple services */}
          {trainerServices.length > 1 && (
            <div className="relative mr-2" ref={serviceDropdownRef}>
              <button
                onClick={() => setIsServiceDropdownOpen(!isServiceDropdownOpen)}
                className="flex items-center px-3 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 border border-cyan-300/50 bg-cyan-900/30"
              >
                <span className="font-medium text-sm mr-2">{activeService || 'Select Service'}</span>
                <FaChevronDown className={`text-white/80 transition-transform ${isServiceDropdownOpen ? 'transform rotate-180' : ''}`} />
              </button>
              {isServiceDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-md shadow-lg py-1 z-50">
                  {trainerServices.map((service) => {
                    const serviceType = servicePartnerTypes[service] || 'instructor';
                    return (
                      <button
                        key={service}
                        onClick={() => handleServiceChange(service)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${activeService === service ? 'bg-cyan-50 text-cyan-700' : 'text-gray-700'
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{service}</span>
                          <span className={`text-xs px-2 py-1 rounded ${serviceType === 'consultant'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-blue-100 text-blue-700'
                            }`}>
                            {serviceType === 'consultant' ? 'Consultant' : 'Instructor'}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <Link
            to="/trainer/programs"
            className="flex items-center px-4 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 group border border-transparent hover:border-cyan-300/50"
          >
            <FaClipboardList className="mr-2 text-white group-hover:text-cyan-300 transition-colors" />
            <span className="font-medium">Programs</span>
          </Link>

          {/* Show Sessions if user has instructor services */}
          {hasInstructorServices && (
            <Link
              to="/trainer/classes"
              className="flex items-center px-4 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 group border border-transparent hover:border-cyan-300/50"
            >
              <FaChalkboardTeacher className="mr-2 text-white group-hover:text-cyan-300 transition-colors" />
              <span className="font-medium">Sessions</span>
            </Link>
          )}

          {/* Show Consultant Hub if user has consultant services */}
          {hasConsultantServices && (
            <Link
              to="/trainer/consultant-dashboard"
              className="flex items-center px-4 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 group border border-transparent hover:border-purple-300/50"
            >
              <FaCalendarAlt className="mr-2 text-white group-hover:text-purple-300 transition-colors" />
              <span className="font-medium">Consultant Hub</span>
            </Link>
          )}
          <Link
            to="/trainer/fee-plan"
            className="flex items-center px-4 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 group border border-transparent hover:border-cyan-300/50"
          >
            <FaFileInvoiceDollar className="mr-2 text-white group-hover:text-cyan-300 transition-colors" />
            <span className="font-medium">Fee Plan</span>
          </Link>
          <Link
            to="/trainer/students"
            className="flex items-center px-4 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 group border border-transparent hover:border-cyan-300/50"
          >
            <FaUsers className="mr-2 text-white group-hover:text-cyan-300 transition-colors" />
            <span className="font-medium">My Members</span>
          </Link>
          <Link
            to="/trainer/fees"
            className="flex items-center px-4 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 group border border-transparent hover:border-cyan-300/50"
          >
            <FaMoneyBillWave className="mr-2 text-white group-hover:text-cyan-300 transition-colors" />
            <span>Fees</span>
          </Link>
          
          <div className="mx-2">
            <NotificationCenter />
          </div>

          {/* Profile Dropdown */}
          <div
            className="relative ml-2"
            ref={profileRef}
            onMouseEnter={handleProfileInteraction}
            onMouseLeave={handleProfileLeave}
          >
            <button
              onClick={isMobile ? () => setIsProfileOpen(!isProfileOpen) : undefined}
              className="flex items-center space-x-2 px-3 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 focus:outline-none border border-transparent hover:border-purple-300/50"
            >
              <div className="relative">
                <Avatar
                  src={trainer?.profileImage}
                  name={trainer?.name || 'Trainer'}
                  size={32}
                  className="border-2 border-cyan-300/70 shadow-lg"
                  alt="Profile"
                />
                {trainer?.status === 'active' && (
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                )}
              </div>
              <FaChevronDown className={`text-white/80 transition-transform ${isProfileOpen ? 'transform rotate-180' : ''}`} />
            </button>

            {isProfileOpen && (
              <div
                className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg py-1 z-50"
                onMouseEnter={handleDropdownEnter}
                onMouseLeave={handleDropdownLeave}
              >
                <div className="px-4 py-3 border-b border-gray-100">
                  <div className="flex items-center mb-2">
                    <FaIdBadge className="text-gray-400 mr-2" />
                    <p className="text-xs text-gray-500">ID: {formatTrainerId(trainer?.trainerId || trainer?.id, trainer?.name) || 'N/A'}</p>
                  </div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-gray-900">{formatName(trainer?.name) || 'Trainer'}</p>
                    {isAdvancePlan && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-semibold rounded-full shadow-md">
                        <FaCrown className="text-[10px]" />
                        <span>Advance</span>
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{trainer?.email || ''}</p>
                </div>
                <Link
                  to="/trainer/trainer-profile"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setIsProfileOpen(false)}
                >
                  <FaUserCircle className="mr-3 text-gray-500" />
                  <span>My Profile</span>
                </Link>
                <Link
                  to="/trainer/settings"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setIsProfileOpen(false)}
                >
                  <FaCog className="mr-3 text-gray-500" />
                  <span>Settings</span>
                </Link>
                {/* Dashboard Toggle in Profile Menu */}
                {hasBothTypes && (
                  <button
                    onClick={() => {
                      handleDashboardToggle();
                      setIsProfileOpen(false);
                    }}
                    className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    {dashboardPreference === 'instructor' ? (
                      <>
                        <FaCalendarAlt className="mr-3 text-gray-500" />
                        <span>Switch to Consultant Dashboard</span>
                      </>
                    ) : (
                      <>
                        <FaChalkboardTeacher className="mr-3 text-gray-500" />
                        <span>Switch to Instructor Dashboard</span>
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={() => {
                    Logout(navigate, setSession);
                    setIsProfileOpen(false);
                  }}
                  className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-100"
                >
                  <FaSignOutAlt className="mr-3 text-gray-500" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Header Right Section */}
        {isMobile && (
          <div className="flex items-center space-x-4">
            <NotificationCenter />
            {/* Mobile Profile Dropdown */}
            <div
              className="relative"
              ref={profileRef}
              onMouseEnter={handleProfileInteraction}
              onMouseLeave={handleProfileLeave}
            >
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="focus:outline-none"
                aria-label="Profile menu"
              >
                <div className="relative">
                  <Avatar
                    src={trainer?.profileImage}
                    name={trainer?.name || 'Trainer'}
                    size={40}
                    className="border-2 border-cyan-300/70 shadow-lg"
                    alt="Profile"
                  />
                  {trainer?.status === 'active' && (
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                  )}
                </div>
              </button>

              {isProfileOpen && (
                <div
                  className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50"
                  onMouseEnter={handleDropdownEnter}
                  onMouseLeave={handleDropdownLeave}
                >
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-gray-900">{formatName(trainer?.name) || 'Trainer'}</p>
                      {isAdvancePlan && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-semibold rounded-full shadow-md">
                          <FaCrown className="text-[10px]" />
                          <span>Advance</span>
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">ID: {formatTrainerId(trainer?.trainerId || trainer?.id, trainer?.name) || 'N/A'}</p>
                  </div>
                  <Link
                    to="/trainer/trainer-profile"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    <FaUserCircle className="mr-3 text-gray-500" />
                    <span>My Profile</span>
                  </Link>
                  <Link
                    to="/trainer/settings"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    <FaCog className="mr-3 text-gray-500" />
                    <span>Settings</span>
                  </Link>

                  {/* Reseller Menu Dropdown for Mobile */}
                  {isReseller && (
                    <div className="border-t border-gray-100">
                      <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Reseller</p>
                      <Link
                        to="/reseller/dashboard"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        <FaChartLine className="mr-3 text-gray-500" />
                        <span>Dashboard</span>
                      </Link>
                      <Link
                        to="/reseller/products"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        <FaStore className="mr-3 text-gray-500" />
                        <span>My Products</span>
                      </Link>
                      <Link
                        to="/reseller/orders"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        <FaBoxOpen className="mr-3 text-gray-500" />
                        <span>My Orders</span>
                      </Link>
                      <Link
                        to="/reseller/earnings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        <FaDollarSign className="mr-3 text-gray-500" />
                        <span>Earnings</span>
                      </Link>
                    </div>
                  )}

                  {/* Dashboard Toggle in Mobile Profile Menu */}
                  {hasBothTypes && (
                    <button
                      onClick={() => {
                        handleDashboardToggle();
                        setIsProfileOpen(false);
                      }}
                      className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {dashboardPreference === 'instructor' ? (
                        <>
                          <FaCalendarAlt className="mr-3 text-gray-500" />
                          <span>Switch to Consultant Dashboard</span>
                        </>
                      ) : (
                        <>
                          <FaChalkboardTeacher className="mr-3 text-gray-500" />
                          <span>Switch to Instructor Dashboard</span>
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      Logout(navigate, setSession);
                      setIsProfileOpen(false);
                    }}
                    className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-100"
                  >
                    <FaSignOutAlt className="mr-3 text-gray-500" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>

          </div>
        )}
      </nav>
    </header>
  );
}

export default TrainerHeader;